/**
 * Shared scaffolding for the visual / mockup family of scenes.
 *
 * All visual scenes share a small set of premium UI shapes:
 *  - Realistic device frames (phone notch, laptop hinge, tablet bezel, watch crown).
 *  - A `screenContent()` helper that renders either uploaded media (image / video)
 *    or a tasteful brand-tinted fallback that "looks like" the Allyver app UI.
 *  - Small inline SVG icons (Lucide-style) without pulling in the React `lucide`
 *    runtime — these become static markup inside the generated HTML scene.
 *
 * Conventions match the bespoke `phoneMockup` / `browserMockup` ports: every
 * helper returns plain HTML strings and never touches the GSAP timeline; the
 * caller composes init/tween lines with `makeTl()` / `gsapSetLine()`.
 */
import { escapeHtml, weights, withAlpha, colors as themeColors, brandName, pickMediaSrc, isVideoMedia } from "../../reel/sceneShared.js";

/* ============================================================================
 * Screen content (uploaded media or brand-tinted fallback)
 * ========================================================================= */

export type ScreenFallbackKind = "app" | "list" | "chat" | "card" | "feed";

/**
 * Returns the inner HTML for a phone / tablet / browser screen. When the user
 * uploaded media at the given index, render it. Otherwise emit a tasteful
 * fallback that hints at the Allyver app without distracting from the layout.
 */
export function screenContent(opts: {
  media: string | string[] | undefined | null;
  mediaIndex?: number;
  accent: string;
  fallback?: ScreenFallbackKind;
  fallbackTitle?: string;
  fallbackLines?: string[];
  cta?: string;
  /**
   * Wie das Medium in den Screen eingepasst wird. Default `cover`, weil
   * die Phone-Frames jetzt exakt auf das iPhone-Verhältnis ausgelegt
   * sind und Screenshots so randlos sitzen. Für Laptop / Tablet, deren
   * Screenshots oft andere Aspekte haben, kann der Aufrufer auf
   * `contain` zurückschalten, um Beschnitt zu vermeiden.
   */
  objectFit?: "cover" | "contain";
}): string {
  const idx = opts.mediaIndex ?? 0;
  const src = pickMediaSrc(opts.media ?? null, idx);
  if (src) {
    const raw = Array.isArray(opts.media) ? opts.media[idx] : opts.media;
    const isVid = isVideoMedia(raw);
    const fit = opts.objectFit ?? "cover";
    // Das `data-has-media`-Attribut hilft den Frame-Helpern (s.u.) zu
    // erkennen, dass schon ein Status-Bar im Screenshot enthalten ist,
    // damit sie ihren eigenen Notch nicht doppelt drüberlegen.
    return isVid
      ? `<video data-has-media="1" src="${escapeHtml(src)}" muted playsinline autoplay loop style="position:absolute;inset:0;width:100%;height:100%;object-fit:${fit};background:#000;display:block;"></video>`
      : `<img data-has-media="1" src="${escapeHtml(src)}" alt="screen" style="position:absolute;inset:0;width:100%;height:100%;object-fit:${fit};background:#000;display:block;" />`;
  }
  return fallbackUi(opts.fallback ?? "app", opts.accent, opts.fallbackTitle ?? brandName, opts.fallbackLines, opts.cta);
}

function fallbackUi(
  kind: ScreenFallbackKind,
  accent: string,
  title: string,
  lines: string[] | undefined,
  cta: string | undefined,
): string {
  const fg = themeColors.fg;
  const bg = themeColors.bg;
  const items = (lines && lines.length ? lines : ["Nahe Anbieter", "Top bewertet", "Schnelle Lieferung", "Persönlich"]).slice(0, 6);
  const ctaText = cta ?? "Jetzt entdecken";

  if (kind === "chat") {
    const sample = ["Hey 👋", "Bist du heute offen?", "Klar, bis 19 Uhr.", "Top, ich komme vorbei."];
    return `<div style="position:absolute;inset:0;display:flex;flex-direction:column;background:linear-gradient(180deg, ${bg} 0%, ${withAlpha(accent, 0.2)} 100%);padding:32px 22px;gap:12px;">
      <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};color:${fg};font-size:22px;margin-bottom:12px;">${escapeHtml(title)}</div>
      ${sample.map((s, i) => `<div style="${i % 2 ? `margin-left:auto;background:${accent};color:${bg};` : `background:${withAlpha(fg, 0.08)};color:${fg};`}max-width:78%;padding:12px 18px;border-radius:20px;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:14px;line-height:1.35;border:1px solid ${withAlpha(fg, 0.06)};box-shadow:0 4px 14px rgba(0,0,0,0.18);">${escapeHtml(s)}</div>`).join("")}
    </div>`;
  }

  if (kind === "feed") {
    return `<div style="position:absolute;inset:0;display:flex;flex-direction:column;background:linear-gradient(180deg, ${bg} 0%, ${withAlpha(accent, 0.18)} 100%);padding:32px 22px;gap:14px;color:${fg};">
      <div style="display:flex;align-items:center;gap:10px;"><div style="width:34px;height:34px;border-radius:9999px;background:${accent};box-shadow:0 0 14px ${withAlpha(accent, 0.5)};"></div><div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:18px;">${escapeHtml(title)}</div></div>
      ${[0,1,2].map((i) => `<div style="background:${withAlpha(fg, 0.06)};border:1px solid ${withAlpha(fg, 0.1)};border-radius:18px;padding:14px;display:flex;flex-direction:column;gap:8px;">
        <div style="height:80px;border-radius:12px;background:linear-gradient(135deg, ${withAlpha(accent, 0.5)} 0%, ${withAlpha(accent, 0.1)} 100%);"></div>
        <div style="height:10px;width:70%;border-radius:5px;background:${withAlpha(fg, 0.18)};"></div>
        <div style="height:8px;width:45%;border-radius:4px;background:${withAlpha(fg, 0.1)};"></div>
      </div>`).join("")}
    </div>`;
  }

  if (kind === "card") {
    return `<div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;background:linear-gradient(160deg, ${bg} 0%, ${withAlpha(accent, 0.32)} 100%);color:${fg};gap:18px;padding:24px;text-align:center;">
      <div style="width:88px;height:88px;border-radius:24px;background:${accent};box-shadow:0 0 60px ${withAlpha(accent, 0.55)};display:flex;align-items:center;justify-content:center;color:${bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:38px;">${escapeHtml((title || "A").slice(0, 1).toUpperCase())}</div>
      <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:26px;letter-spacing:-1px;line-height:1.05;">${escapeHtml(title)}</div>
      <div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:14px;color:${withAlpha(fg, 0.75)};">★★★★★  ·  4.9</div>
    </div>`;
  }

  if (kind === "list") {
    return `<div style="position:absolute;inset:0;display:flex;flex-direction:column;background:linear-gradient(180deg, ${bg} 0%, ${withAlpha(accent, 0.22)} 100%);padding:32px 22px;gap:14px;color:${fg};">
      <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:20px;margin-bottom:6px;">${escapeHtml(title)}</div>
      ${items.map((it) => `<div style="display:flex;align-items:center;gap:12px;background:${withAlpha(fg, 0.06)};border:1px solid ${withAlpha(fg, 0.1)};border-radius:16px;padding:12px 14px;">
        <div style="width:30px;height:30px;border-radius:9999px;background:${accent};box-shadow:0 0 12px ${withAlpha(accent, 0.45)};"></div>
        <div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:14px;">${escapeHtml(it)}</div>
      </div>`).join("")}
    </div>`;
  }

  return `<div style="position:absolute;inset:0;display:flex;flex-direction:column;background:linear-gradient(180deg, ${bg} 0%, ${withAlpha(accent, 0.28)} 100%);padding:32px 22px;gap:14px;color:${fg};">
    <div style="display:flex;align-items:center;justify-content:space-between;">
      <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:22px;letter-spacing:-0.5px;">${escapeHtml(title)}</div>
      <div style="width:34px;height:34px;border-radius:9999px;background:${withAlpha(fg, 0.08)};border:1px solid ${withAlpha(fg, 0.12)};"></div>
    </div>
    <div style="display:flex;gap:8px;flex-wrap:wrap;">${["Alle","Restaurants","Shops","Service"].map((c, i) => `<div style="padding:8px 14px;border-radius:9999px;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:13px;${i === 0 ? `background:${accent};color:${bg};` : `background:${withAlpha(fg, 0.08)};color:${fg};border:1px solid ${withAlpha(fg, 0.12)};`}">${c}</div>`).join("")}</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;flex:1;">${[0,1,2,3].map(() => `<div style="background:${withAlpha(fg, 0.06)};border-radius:16px;border:1px solid ${withAlpha(fg, 0.1)};padding:10px;display:flex;flex-direction:column;gap:6px;">
      <div style="flex:1;border-radius:10px;background:linear-gradient(135deg, ${withAlpha(accent, 0.55)} 0%, ${withAlpha(accent, 0.12)} 100%);"></div>
      <div style="height:8px;width:70%;border-radius:4px;background:${withAlpha(fg, 0.2)};"></div>
      <div style="height:6px;width:40%;border-radius:3px;background:${withAlpha(fg, 0.12)};"></div>
    </div>`).join("")}</div>
    <div style="margin-top:auto;background:${accent};color:${bg};border-radius:9999px;padding:12px 14px;text-align:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:14px;box-shadow:0 0 30px ${withAlpha(accent, 0.55)};">${escapeHtml(ctaText)}</div>
  </div>`;
}

/* ============================================================================
 * Device frames
 * ========================================================================= */

export function phoneFrame(opts: {
  id: string;
  width: number;
  accent: string;
  inner: string;
  tilt?: number;
  showNotch?: boolean;
}): string {
  const w = opts.width;
  // Bezel/Padding um den Bildschirm (≈3.5% von Phone-Breite). Der INNERE
  // Screen ist auf modernes iPhone-Verhältnis (1290:2796 ≈ 19.5:9)
  // ausgelegt, damit echte iPhone-Screenshots PIXELGENAU in den Screen
  // passen — kein Beschnitt, keine Balken (cover === contain bei
  // identischem Aspect-Ratio).
  const bezel = Math.round(w * 0.035);
  const innerW = w - bezel * 2;
  const innerH = Math.round(innerW * (2796 / 1290));
  const h = innerH + bezel * 2;
  const tilt = opts.tilt ?? 0;
  // Wenn der innere HTML einen <img>/<video> mit unserem `data-has-media`-
  // Marker enthält, ist der Screenshot bereits ein vollständiger iPhone-
  // Frame inkl. eigener System-Statusleiste — dann lassen wir unseren
  // eigenen Notch weg, damit er nicht doppelt erscheint.
  const innerHasMedia = /data-has-media="1"/.test(opts.inner);
  const notch = (opts.showNotch ?? true) && !innerHasMedia;
  return `<div id="${opts.id}" style="position:relative;width:${w}px;height:${h}px;background:linear-gradient(180deg,#1a1a1c 0%,#0a0a0b 100%);border-radius:${Math.round(w * 0.13)}px;padding:${bezel}px;box-shadow:0 0 90px ${withAlpha(opts.accent, 0.35)},0 60px 130px rgba(0,0,0,0.55), inset 0 0 0 1.5px ${withAlpha(themeColors.fg, 0.08)};border:1.5px solid ${withAlpha(themeColors.fg, 0.18)};box-sizing:border-box;${tilt ? `transform:rotate(${tilt}deg);` : ""}">
    <div style="position:relative;width:100%;height:100%;border-radius:${Math.round(innerW * 0.11)}px;overflow:hidden;background:${innerHasMedia ? "#000" : themeColors.bg};box-sizing:border-box;">${opts.inner}${notch ? `<div style="position:absolute;top:${Math.round(innerW * 0.035)}px;left:50%;transform:translateX(-50%);width:${Math.round(innerW * 0.34)}px;height:${Math.round(innerW * 0.085)}px;background:#000;border-radius:9999px;z-index:5;box-shadow:0 0 0 1px rgba(255,255,255,0.04);"></div>` : ""}</div>
  </div>`;
}

export function laptopFrame(opts: {
  id: string;
  width: number;
  accent: string;
  inner: string;
}): string {
  const w = opts.width;
  const h = Math.round(w * 0.6);
  const baseW = Math.round(w * 1.15);
  return `<div id="${opts.id}" style="position:relative;display:flex;flex-direction:column;align-items:center;">
    <div style="position:relative;width:${w}px;height:${h}px;background:#0e1117;border-radius:18px;padding:14px;box-shadow:0 0 80px ${withAlpha(opts.accent, 0.3)},0 50px 110px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05);border:1.5px solid ${withAlpha(themeColors.fg, 0.14)};">
      <div style="position:absolute;top:18px;left:50%;transform:translateX(-50%);width:54px;height:6px;background:#000;border-radius:9999px;"></div>
      <div style="position:relative;width:100%;height:100%;border-radius:8px;overflow:hidden;background:${themeColors.bg};">${opts.inner}</div>
    </div>
    <div style="width:${baseW}px;height:14px;background:linear-gradient(180deg,#1c2129,#0e1117);border-radius:0 0 24px 24px;box-shadow:0 16px 30px rgba(0,0,0,0.55);position:relative;">
      <div style="position:absolute;top:0;left:50%;transform:translateX(-50%);width:18%;height:6px;background:#000;border-radius:0 0 12px 12px;"></div>
    </div>
  </div>`;
}

export function tabletFrame(opts: {
  id: string;
  width: number;
  height: number;
  accent: string;
  inner: string;
}): string {
  return `<div id="${opts.id}" style="position:relative;width:${opts.width}px;height:${opts.height}px;background:linear-gradient(180deg,#0e1117,#1a1e25);border-radius:36px;padding:16px;box-shadow:0 0 90px ${withAlpha(opts.accent, 0.32)},0 60px 130px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05);border:1.5px solid ${withAlpha(themeColors.fg, 0.14)};">
    <div style="position:absolute;left:8px;top:50%;transform:translateY(-50%);width:4px;height:60px;background:#000;border-radius:4px;"></div>
    <div style="position:relative;width:100%;height:100%;border-radius:22px;overflow:hidden;background:${themeColors.bg};">${opts.inner}</div>
  </div>`;
}

export function watchFrame(opts: {
  id: string;
  width: number;
  accent: string;
  inner: string;
}): string {
  const w = opts.width;
  const h = Math.round(w * 1.18);
  return `<div id="${opts.id}" style="position:relative;width:${w}px;height:${h}px;background:linear-gradient(180deg,#0e1117,#1a1e25);border-radius:${Math.round(w * 0.26)}px;padding:14px;box-shadow:0 0 80px ${withAlpha(opts.accent, 0.4)},0 40px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06);border:1.5px solid ${withAlpha(themeColors.fg, 0.16)};">
    <div style="position:absolute;right:-6px;top:30%;width:10px;height:36px;background:#1c2129;border-radius:4px;box-shadow:inset -1px 0 0 rgba(255,255,255,0.1);"></div>
    <div style="position:absolute;right:-5px;top:58%;width:8px;height:18px;background:#1c2129;border-radius:3px;"></div>
    <div style="position:absolute;left:-8px;top:8%;width:60%;height:18px;background:linear-gradient(180deg,#11151c,#1a1e25);border-radius:9px 0 0 9px;transform:translateY(-100%);"></div>
    <div style="position:absolute;left:-8px;bottom:8%;width:60%;height:18px;background:linear-gradient(0deg,#11151c,#1a1e25);border-radius:0 0 0 9px;transform:translateY(100%);"></div>
    <div style="position:relative;width:100%;height:100%;border-radius:${Math.round(w * 0.2)}px;overflow:hidden;background:${themeColors.bg};">${opts.inner}</div>
  </div>`;
}

/* ============================================================================
 * Tiny inline SVG icons (Lucide-style, single stroke) — no React runtime.
 * ========================================================================= */

const ICON_PATHS: Record<string, string> = {
  bolt: "M13 2 3 14h7l-1 8 10-12h-7l1-8z",
  check: "M20 6 9 17l-5-5",
  heart: "M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z",
  star: "M12 2l3 7h7l-5.5 4.5 2 7L12 16l-6.5 4.5 2-7L2 9h7l3-7z",
  shop: "M3 9 4 4h16l1 5M3 9v10h18V9M3 9h18",
  taxi: "M5 17h14M5 11l2-5h10l2 5M3 17v-3h18v3M7 17v2M17 17v2",
  hotel: "M3 21V8l9-5 9 5v13M9 21v-6h6v6M9 11h.01M15 11h.01",
  event: "M3 5h18v16H3zM8 3v4M16 3v4M3 9h18",
  service: "M14.7 6.3a4 4 0 0 0-5.4-5.4L4 6.2l2.1 2.1-2.1 2.1 7.8 7.8 2.1-2.1 2.1 2.1 5.3-5.3a4 4 0 0 0-5.4-5.4z",
  chat: "M21 11.5a8.4 8.4 0 0 1-3.7 7L17 22l-3.5-2A8.5 8.5 0 1 1 21 11.5z",
  lock: "M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4",
  globe: "M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zM2 12h20M12 2a14 14 0 0 1 0 20M12 2a14 14 0 0 0 0 20",
  gift: "M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7l-3-3a3 3 0 0 1 3-3 3 3 0 0 1 3 3l-3 3",
  rocket: "M4.5 16.5c-1.5 1.5-2 5-2 5s3.5-.5 5-2c.85-.85.95-2.25.16-3.16a2 2 0 0 0-3.16.16zM12 15l-3-3 8.5-8.5a4.95 4.95 0 0 1 3 3L12 15zM9 12 4 9V4l5 1 3 3M15 12l3 5h-5l-1-5",
  chart: "M3 21h18M5 21V10m4 11V6m4 15v-9m4 9V8m4 13V13",
  map: "M9 4 4 6v14l5-2 6 2 5-2V4l-5 2-6-2zM9 4v14M15 6v14",
  clock: "M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zM12 6v6l4 2",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  pin: "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0zM12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  shield: "M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z",
  badge: "M12 2 9 6H4l2.5 4L4 14h5l3 4 3-4h5l-2.5-4L20 6h-5l-3-4z",
  credit: "M2 7h20v10H2zM2 11h20M6 15h4",
  flag: "M4 22V3M4 3h11l-2 4 2 4H4",
  headphones: "M3 14a9 9 0 1 1 18 0v3a2 2 0 0 1-2 2h-2v-7h4M3 14h4v7H5a2 2 0 0 1-2-2v-5z",
  zap: "M13 2 3 14h7l-1 8 10-12h-7l1-8z",
  store: "M3 9 4 4h16l1 5M3 9v10h18V9M3 9h18M9 19v-5h6v5",
  truck: "M3 16V6h11v10M14 9h4l3 3v4h-7M3 16h4M14 16h0M7 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM19 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0z",
  euro: "M18 5a8 8 0 1 0 0 14M3 10h13M3 14h13",
  trending: "M3 17l6-6 4 4 8-8M17 7h4v4",
  award: "M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM8 14l-2 8 6-3 6 3-2-8",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4-4",
  message: "M21 11.5a8.4 8.4 0 0 1-3.7 7L17 22l-3.5-2A8.5 8.5 0 1 1 21 11.5z",
  phone: "M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z",
  smartphone: "M5 2h14v20H5zM12 18h.01",
  flame: "M8 14c0-2 1-4 3-5 0 3 2 4 2 7a3 3 0 1 1-6 0c0-1 .3-1.5 1-2zM12 2C14 6 18 8 18 13a6 6 0 0 1-12 0c0-2 1-4 2-5 0 3 4 1 4-6z",
  thumbsup: "M14 9V5a3 3 0 0 0-6 0c0 2-2 4-5 4v11h12a3 3 0 0 0 3-3l1-6a3 3 0 0 0-3-3z",
  users: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
};

export function lucideIcon(name: string, size = 22, color = "currentColor", strokeWidth = 2): string {
  const path = ICON_PATHS[name.toLowerCase()] ?? ICON_PATHS.star;
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="${path}"/></svg>`;
}

/* ============================================================================
 * Misc helpers
 * ========================================================================= */

/** Eyebrow + headline + subhead block — used by ~half of the layout scenes. */
export function sceneHeader(opts: {
  clipPrefix: string;
  eyebrow?: string;
  title?: string;
  sub?: string;
  accent: string;
  titlePx: number;
  subPx?: number;
  eyebrowPx?: number;
  align?: "left" | "center";
  maxWidthPx?: number;
}): { html: string; ids: { eyebrow: string; title: string; sub: string } } {
  const a = opts.align ?? "center";
  const titlePx = opts.titlePx;
  const subPx = opts.subPx ?? Math.round(titlePx * 0.32);
  const eyePx = opts.eyebrowPx ?? Math.max(14, Math.round(titlePx * 0.16));
  const mw = opts.maxWidthPx ?? 1100;
  const ids = {
    eyebrow: `${opts.clipPrefix}-eye`,
    title: `${opts.clipPrefix}-title`,
    sub: `${opts.clipPrefix}-sub`,
  };
  const html = `<div style="display:flex;flex-direction:column;align-items:${a === "center" ? "center" : "flex-start"};gap:14px;text-align:${a};max-width:${mw}px;">
    ${opts.eyebrow ? `<div id="${ids.eyebrow}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyePx}px;letter-spacing:6px;text-transform:uppercase;color:${opts.accent};display:inline-flex;align-items:center;gap:14px;">
      <span style="width:36px;height:2px;background:${opts.accent};display:inline-block;"></span>
      <span>${escapeHtml(opts.eyebrow)}</span>
    </div>` : ""}
    ${opts.title ? `<div id="${ids.title}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titlePx}px;line-height:1.02;letter-spacing:-2px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(opts.title)}</div>` : ""}
    ${opts.sub ? `<div id="${ids.sub}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subPx}px;letter-spacing:-0.2px;line-height:1.32;color:${withAlpha(themeColors.fg, 0.78)};text-wrap:balance;">${escapeHtml(opts.sub)}</div>` : ""}
  </div>`;
  return { html, ids };
}

/** Parses `"A | B | C"` items into ordered tuples (trim/empty-safe). */
export function splitItem(raw: string, sep = "|"): string[] {
  return raw.split(sep).map((s) => s.trim());
}
